import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Honour deep-links: if the user hit a protected URL (e.g. /mtd-reconciler)
  // while logged out, PrivateRoute can push them to /login with state.from
  // set. Fall back to dashboard.
  const redirectTarget = location.state?.from?.pathname || location.state?.from || '/';

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgot(false);
    setForgotEmail('');
    setForgotSuccess(false);
    setForgotError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D2C54 0%, #1A5490 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <img src="/img/acs-nav-logo.png" alt="ACS" style={{ width: 80 }} onError={(e) => { e.target.style.display = 'none'; }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
              ACS Command Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Smarter By Design
            </Typography>
          </Box>

          {!showForgot ? (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} autoComplete="on">
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  name="email"
                  id="login-email"
                  inputProps={{
                    autoComplete: 'username',
                    autoCapitalize: 'none',
                    autoCorrect: 'off',
                    spellCheck: false,
                    inputMode: 'email',
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  name="password"
                  id="login-password"
                  inputProps={{
                    autoComplete: 'current-password',
                    autoCapitalize: 'none',
                    autoCorrect: 'off',
                    spellCheck: false,
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              </Box>

              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowForgot(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot your password?
                </Link>
              </Typography>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Contact your administrator if you need access.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                Reset Your Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {forgotSuccess ? (
                <>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    If that email exists, a reset link has been sent. Check your inbox.
                  </Alert>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleBackToLogin}
                    sx={{ mt: 1 }}
                  >
                    Back to Sign In
                  </Button>
                </>
              ) : (
                <>
                  {forgotError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {forgotError}
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleForgotSubmit}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      margin="normal"
                      required
                      autoFocus
                    />
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={forgotLoading}
                      sx={{ mt: 3, mb: 2, py: 1.5 }}
                    >
                      {forgotLoading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleBackToLogin}
                      sx={{ cursor: 'pointer' }}
                    >
                      Back to Sign In
                    </Link>
                  </Typography>
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
